import React from 'react';
import Container from '../common/Container';
import Header from '../layout/Header';
import Button from '../common/Button';
import { UserSelection } from '../../types';

interface ResultsScreenProps {
  score: number;
  totalRounds: number;
  userSelections: UserSelection[];
  accuracy: number;
  onPlayAgain?: (() => void) | null;
  onBackToMenu: () => void;
  hasPlayedToday?: boolean;
  gameDate?: Date;
  requiredCorrectAnswers?: number;
  nextGameAvailableAt?: Date;
  releaseScheduleLabel?: string;
  releaseTimeZone?: string;
  mode?: 'text' | 'picture';
}

const ResultsScreen: React.FC<ResultsScreenProps> = ({
  score,
  totalRounds,
  userSelections,
  accuracy,
  onPlayAgain,
  onBackToMenu,
  hasPlayedToday = false,
  gameDate,
  requiredCorrectAnswers = 8,
  nextGameAvailableAt,
  releaseScheduleLabel,
  releaseTimeZone,
  mode = 'text',
}) => {

  // Determine if game was won
  const gameWon = score >= requiredCorrectAnswers;

  const getNextGameTimeDetails = () => {
    if (!nextGameAvailableAt) {
      return null;
    }

    const localTimeFormatter = new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const localDateFormatter = new Intl.DateTimeFormat(undefined, {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
    });

    let officialLabel = releaseScheduleLabel || null;
    if (!officialLabel && releaseTimeZone) {
      const scheduleFormatter = new Intl.DateTimeFormat('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: releaseTimeZone,
        timeZoneName: 'short',
      });
      officialLabel = scheduleFormatter.format(nextGameAvailableAt);
    }

    return {
      localTime: localTimeFormatter.format(nextGameAvailableAt).replace(/\s+/g, ' ').trim(),
      localDate: localDateFormatter.format(nextGameAvailableAt),
      officialLabel,
    };
  };

  const nextGameTimeDetails = getNextGameTimeDetails();
  const formatNextGameMessage = (prefix: string, fallback: string) => {
    if (!nextGameTimeDetails) {
      return fallback;
    }

    const primaryTime = nextGameTimeDetails.officialLabel || nextGameTimeDetails.localTime;
    let message = `${prefix} ${primaryTime}`;
    if (nextGameTimeDetails.localDate) {
      message += ` on ${nextGameTimeDetails.localDate}`;
    }
    return `${message}.`;
  };

  const nextChallengeMessage = formatNextGameMessage(
    'Neural networks recharged. Next challenge unlocks at',
    'Neural networks recharged. Return in 24 hours for a new challenge.'
  );
  const systemScheduleMessage = formatNextGameMessage(
    'System will generate new challenge parameters at',
    'System will generate new challenge parameters in 24:00:00'
  );

  const title = mode === 'picture' ? 'Visual Analysis Results' : 'Mission Results';

  return (
    <div className="min-h-screen bg-dark-950 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 bg-circuit opacity-5"></div>
      <div className="absolute top-0 left-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse-slow"></div>
      <div className="absolute bottom-0 right-1/4 w-48 h-48 sm:w-96 sm:h-96 bg-secondary-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>

      <Header title={title} mode={mode} />

      <Container maxWidth="md" className="py-6 sm:py-12 px-4 sm:px-6 relative z-10">
        {/* Main Results Card */}
        <div className="card-robotic p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 relative">
          {/* Status indicator */}
          <div className="absolute top-3 sm:top-4 right-3 sm:right-4 flex items-center space-x-1 sm:space-x-2">
            <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full animate-pulse ${gameWon ? 'bg-accent-500' : 'bg-red-500'}`}></div>
            <span className="font-mono text-xs text-dark-300 tracking-wider uppercase">
              {gameWon ? 'SUCCESS' : 'FAILED'}
            </span>
          </div>

          <div className="text-center mb-6 sm:mb-8">
            <div className="relative inline-block mb-4 sm:mb-6">
              <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-wider uppercase ${gameWon
                ? 'text-transparent bg-clip-text bg-gradient-to-r from-accent-400 to-primary-400'
                : 'text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-secondary-400'
                }`}>
                <span className="hidden sm:inline">{gameWon ? 'MISSION ACCOMPLISHED' : 'MISSION FAILED'}</span>
                <span className="sm:hidden">{gameWon ? 'SUCCESS' : 'FAILED'}</span>
              </h1>
              <div className={`absolute -inset-2 sm:-inset-4 blur-xl rounded-lg ${gameWon ? 'bg-gradient-to-r from-accent-500/20 to-primary-500/20' : 'bg-gradient-to-r from-red-500/20 to-secondary-500/20'
                }`}></div>
            </div>

            <div className="mb-4 sm:mb-6">
              <div className={`inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 rounded-lg border shadow-lg ${gameWon
                ? 'bg-gradient-to-r from-accent-600 to-accent-700 border-accent-500 shadow-[0_0_20px_rgba(34,197,94,0.3)]'
                : 'bg-gradient-to-r from-red-600 to-red-700 border-red-500 shadow-[0_0_20px_rgba(239,68,68,0.3)]'
                }`}>
                <div className={`w-2 h-2 rounded-full animate-pulse mr-2 sm:mr-3 ${gameWon ? 'bg-white' : 'bg-white'}`}></div>
                <span className="font-display font-semibold text-white tracking-wider uppercase text-xs sm:text-sm">
                  {gameWon ? 'TARGET ACHIEVED' : 'TARGET MISSED'}
                </span>
              </div>

              {hasPlayedToday && gameDate ? (
                <div className="mt-3 sm:mt-4 bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-700 rounded-lg p-3 sm:p-4">
                  <p className="text-primary-300 font-mono text-xs sm:text-sm">
                    {gameDate.toLocaleDateString('en-US', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              ) : (
                <div className="mt-3 sm:mt-4 bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-700 rounded-lg p-3 sm:p-4">
                  <p className="text-dark-200 font-mono text-xs sm:text-sm">
                    {gameWon
                      ? `TARGET: ${requiredCorrectAnswers} • ACHIEVED`
                      : `TARGET: ${requiredCorrectAnswers} • GOT: ${score}`
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Accuracy Display */}
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className={`relative w-32 h-32 sm:w-40 sm:h-40 lg:w-48 lg:h-48 rounded-full border-4 flex items-center justify-center ${gameWon
              ? 'border-accent-500 bg-gradient-to-br from-accent-950 to-dark-900 shadow-[0_0_30px_rgba(34,197,94,0.3)]'
              : 'border-red-500 bg-gradient-to-br from-red-950 to-dark-900 shadow-[0_0_30px_rgba(239,68,68,0.3)]'
              }`}>
              {/* Animated border */}
              <div className={`absolute inset-0 rounded-full border-2 animate-pulse ${gameWon ? 'border-accent-400/50' : 'border-red-400/50'
                }`}></div>

              <div className="text-center relative z-10">
                <div className={`text-3xl sm:text-4xl lg:text-5xl font-display font-bold ${gameWon ? 'text-accent-400' : 'text-red-400'
                  }`}>
                  {accuracy}%
                </div>
                <div className="text-dark-300 font-mono text-xs sm:text-sm mt-1 tracking-wider uppercase">
                  Accuracy
                </div>
              </div>

              {/* Corner accents - smaller on mobile */}
              <div className={`absolute top-1 sm:top-2 left-1 sm:left-2 w-3 h-3 sm:w-4 sm:h-4 border-l-2 border-t-2 ${gameWon ? 'border-accent-400' : 'border-red-400'
                }`}></div>
              <div className={`absolute top-1 sm:top-2 right-1 sm:right-2 w-3 h-3 sm:w-4 sm:h-4 border-r-2 border-t-2 ${gameWon ? 'border-accent-400' : 'border-red-400'
                }`}></div>
              <div className={`absolute bottom-1 sm:bottom-2 left-1 sm:left-2 w-3 h-3 sm:w-4 sm:h-4 border-l-2 border-b-2 ${gameWon ? 'border-accent-400' : 'border-red-400'
                }`}></div>
              <div className={`absolute bottom-1 sm:bottom-2 right-1 sm:right-2 w-3 h-3 sm:w-4 sm:h-4 border-r-2 border-b-2 ${gameWon ? 'border-accent-400' : 'border-red-400'
                }`}></div>
            </div>
          </div>

          {/* Score Display */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-700 rounded-xl p-4 sm:p-6 shadow-lg">
              <div className={`text-3xl sm:text-4xl font-display font-bold mb-2 ${gameWon ? 'text-accent-400' : 'text-red-400'
                }`}>
                {score} / {totalRounds}
              </div>
              <p className="text-dark-300 font-mono text-xs sm:text-sm tracking-wider uppercase mb-2">
                Correct Identifications
              </p>
              <div className={`inline-flex items-center px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-mono ${gameWon
                ? 'bg-accent-500/20 text-accent-300 border border-accent-500/30'
                : 'bg-red-500/20 text-red-300 border border-red-500/30'
                }`}>
                <span className="hidden sm:inline">
                  {gameWon
                    ? `VICTORY: ${score}/${requiredCorrectAnswers} TARGET ACHIEVED`
                    : `DEFEAT: ${score}/${requiredCorrectAnswers} TARGET MISSED`
                  }
                </span>
                <span className="sm:hidden">
                  {gameWon ? `WON: ${score}/${requiredCorrectAnswers}` : `LOST: ${score}/${requiredCorrectAnswers}`}
                </span>
              </div>
            </div>
          </div>


          {hasPlayedToday && (
            <div className="text-center mb-8 bg-gradient-to-r from-primary-600 to-primary-700 border border-primary-500 rounded-xl p-6 shadow-neon">
              <div className="flex items-center justify-center mb-2">
                <div className="w-2 h-2 bg-accent-400 rounded-full animate-pulse mr-3"></div>
                <span className="font-display font-semibold text-white tracking-wider uppercase text-sm">
                  Daily Challenge Complete
                </span>
              </div>
              <p className="text-primary-200 font-mono text-sm">
                {nextChallengeMessage}
              </p>
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4">
          <Button
            onClick={onBackToMenu}
            variant="secondary"
            size="large"
            className="w-full sm:w-auto"
          >
            BACK TO MENU
          </Button>

          {onPlayAgain ? (
            <Button
              onClick={onPlayAgain}
              variant="primary"
              size="large"
              className="relative group w-full sm:w-auto"
            >
              <div className="flex items-center justify-center space-x-3">
                <span className="font-display tracking-wider">RETRY MISSION</span>
                <div className="w-0 h-0 border-l-4 border-l-white border-y-2 border-y-transparent group-hover:translate-x-1 transition-transform"></div>
              </div>
            </Button>
          ) : (
            !hasPlayedToday && (
              <div className="bg-gradient-to-br from-dark-800 to-dark-900 border border-dark-700 rounded-xl p-6 shadow-lg w-full sm:w-auto">
                <div className="flex items-center justify-center mb-2">
                  <div className="w-2 h-2 bg-primary-400 rounded-full animate-pulse mr-3"></div>
                  <span className="font-display font-semibold text-primary-300 tracking-wider uppercase text-sm">
                    Mission Data Archived
                  </span>
                </div>
                <p className="text-dark-300 font-mono text-sm">
                  {systemScheduleMessage}
                </p>
              </div>
            )
          )}
        </div>
      </Container>
    </div>
  );
};

export default ResultsScreen;
